import { Component, OnInit } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { FormControl } from '@angular/forms';
import {IcDataService} from "../../services/ic-data.service";
import {JSON2CSVService} from "../../services/JSON2CSV.service";

interface DataRow {
    field: string;
    [key: string]: any; // Dynamic keys representing securityProfileId
}

@Component({
    selector: 'profile-matrix',
    templateUrl: './profile-matrix.component.html',
    styleUrls: ['./profile-matrix.component.scss']
})
export class ProfileMatrixComponent implements OnInit {
    allProfiles: any;
    selectedProfiles = new FormControl([]);
    data: DataRow[] = [];
    displayedColumns: string[] = ['field'];
    dataSource = new MatTableDataSource(this.data);
    showDifferences = true;
    isSaving = false;
    public securityError = false;
    private headerRow: DataRow;


    constructor(
        private _icDataService: IcDataService,
        private _json2csv: JSON2CSVService
    ) {}

    ngOnInit(): void {
        this._icDataService.getProfiles().subscribe({
            next: (profiles) => {
                this.allProfiles = profiles;
                this.updateDisplayedColumns();
            },
            error: (error) => {
                if (error.status === 403 ){
                    {
                        this.securityError=true;
                    }
                }
            }
        }
        );
    }

    updateDisplayedColumns(): void {
        const selectedIds = this.selectedProfiles.value;
        this.displayedColumns = ['field', ...selectedIds.map(id => `${id}`)];
        this.buildDataRows(selectedIds);
        this.toggleChanges();
    }

    buildDataRows(selectedIds: number[]): void {
        const rows: DataRow[] = [];
        const profiles = this.allProfiles.filter(profile => selectedIds.includes(profile.securityProfileId));

        const fields = ['securityProfileId', 'SecurityProfileName', 'description', 'SecurityProfileStatusName', 'isInternal'];
        fields.forEach(field => {
            const row: DataRow = { field };
            profiles.forEach(profile => {
                row[`${profile.securityProfileId}`] = profile[field] || 'None';
            });
            if(row.field === 'SecurityProfileName'){this.headerRow = row;}
            rows.push(row);
        });

        const permissionKeys = Array.from(new Set(profiles.flatMap(p => p.permissions?.map(per => per.permissionKey) || [])));
        permissionKeys.forEach(key => {
            const row: DataRow = { field: key.toString() };
            profiles.forEach(profile => {
                const permission = profile.permissions?.find(per => per.permissionKey === key);
                row[`${profile.securityProfileId}`] = permission ? permission.optionKeys.join(', ') : 'None';
            });
            rows.push(row);
        });

        // Handle Data Restrictions
        const restrictionKeys = Array.from(new Set(profiles.flatMap(p => p.dataRestrictions?.map(res => res.dataRoleEntityTypeName) || [])));
        restrictionKeys.forEach(key => {
            const row: DataRow = { field: `Data Restriction: ${key}` };
            profiles.forEach(profile => {
                const restriction = profile.dataRestrictions?.find(res => res.dataRoleEntityTypeName === key);
                row[`${profile.securityProfileId}`] = restriction ? restriction.accessibilityType + restriction.entityIds : 'None';
            });
            rows.push(row);
        });

        this.data = rows;
        this.dataSource.data = rows;
    }

    downloadMatrix(){
        const opts = {
            fields: [
                { label: 'Profile ID', value: 'field' },
                ...this.displayedColumns.slice(1)], // Ensure 'field' is first, followed by other columns
        };
        this._json2csv.downloadFile( this._json2csv.convertToCSV(this.dataSource.data, opts), 'Profile Comparison', );
    }

    toggleChanges(): void {
        if (this.showDifferences && this.selectedProfiles.value.length != 1) {
            this.dataSource.data = this.data.filter(row => {
                const values = this.displayedColumns.slice(1).map(col => this.normalizeCSV(row[col]));
                return new Set(values).size > 1;
            });
        } else {

            this.dataSource.data = this.data;
        }
    }

    normalizeCSV(value: string): string {
        //if (typeof value === 'string' || value instanceof String)
        return typeof value === 'string' ? value.split(', ').map(v => v.trim()).sort().join(', ') : 'None';
    }

    isChanged(row: DataRow, key: string): boolean {
        return key !== 'field' && row[key] !== this.data[0][key];
    }

    editValue(row: DataRow, key: string, event: any): void {
        row[key] = event.target.value;
    }

    copyValue(sourceRow: DataRow, targetKey: string): void {
        this.data.forEach(row => {
            if (row.field === sourceRow.field) {
                row[targetKey] = sourceRow[targetKey];
            }
        });
    }

    async saveChanges(): Promise<void> {
        this.isSaving = true;
        for (const row of this.data) {
            await this.saveRowToAPI(row);
        }
        this.isSaving = false;
        alert('Changes have been saved successfully!');
    }

    saveRowToAPI(row: DataRow): Promise<void> {
        return new Promise(resolve => {
            setTimeout(() => {
                console.log('Saved:', row);
                resolve();
            }, 500);
        });
    }

    getvalueArray(rowElement: any) {
        if (typeof rowElement === 'string' ) {
            return rowElement.split(',').map(v => v.replace('custom',''));
        }
        else{
            return [rowElement];
        }
    }

    formatHeaderTitle(columnKey: string): string {
        const headerName = this.headerRow?.[columnKey] || columnKey;
        return headerName !== 'SecurityProfileName' ? headerName : '';
    }
}
